/**
 * Professionals Query Hooks
 * Hooks for fetching professionals with filtering, sorting, and pagination
 */

import { apiClient } from '@shared/lib/api/client';
import { API_ENDPOINTS } from '@shared/lib/api/endpoints';
import type { PaginatedResponse, Professional, ProfessionalFilters } from '@shared/types';
import { useQuery } from '@tanstack/react-query';
export type { PaginatedResponse }; // Re-export for consumers

// ============================================================================
// API Response Types (from reference app)
// ============================================================================

interface ApiProfessional {
  id: string
  name: string
  picture?: string | { path: string; _id: string; filename: string } // Can be string OR object with path
  city?: { id: string; name?: string }[]
  cityId?: string
  gender?: 'male' | 'female' | null
  activeAccount?: boolean
  bio?: string
  professions?: { id: string; name: string }[] | string // Can be array OR string
  rate?: number | string | { $numberDecimal: string } // Can be number, string, or MongoDB decimal
  comments?: number
  goodProfessionalRate?: { $numberDecimal: string }
  completed?: number
  socials?: {
    instagram?: string
    facebook?: string
    googleMaps?: string
    twitter?: string
    linkedin?: string
    tiktok?: string
  }
  provider?: {
    name?: string
    lastName?: string
    phoneNumber?: string
  }
  contact?: string // Backend returns professionalContact as direct string, not nested object
  professionalContact?: string // Alias for contact field
  status?: 'UNCLAIMED' | 'CLAIMED' | 'REMOVED' // Profile claiming status
}

// ============================================================================
// Mappers
// ============================================================================

function mapApiProfessionalToProfessional(apiProf: ApiProfessional): Professional {

  // Parse rating - can be number, string, or MongoDB decimal object
  let rating = 0
  if (typeof apiProf.rate === 'number') {
    rating = apiProf.rate
  } else if (typeof apiProf.rate === 'string') {
    rating = parseFloat(apiProf.rate) || 0
  } else if (apiProf.rate && typeof apiProf.rate === 'object' && '$numberDecimal' in apiProf.rate) {
    rating = parseFloat(apiProf.rate.$numberDecimal) || 0
  }

  // Handle professions - can be array of objects OR string
  let professionObj: { _id: string; name: string; description: string; order: number; count: number; createdAt: string; updatedAt: string } | undefined
  let professionId = ''

  if (typeof apiProf.professions === 'string') {
    // Detail endpoint returns professions as string
    professionObj = {
      _id: '',
      name: apiProf.professions,
      description: '',
      order: 0,
      count: 0,
      createdAt: '',
      updatedAt: '',
    }
  } else if (Array.isArray(apiProf.professions) && apiProf.professions.length > 0) {
    // Search endpoint returns professions as array
    professionId = apiProf.professions[0]!.id
    professionObj = {
      _id: apiProf.professions[0]!.id,
      name: apiProf.professions[0]!.name,
      description: '',
      order: 0,
      count: 0,
      createdAt: '',
      updatedAt: '',
    }
  }

  // Handle city - extract name if available
  const cityName = apiProf.city?.[0]?.name || ''
  const cityId = apiProf.cityId || apiProf.city?.[0]?.id || ''

  // Handle picture - use backend proxy endpoint instead of direct B2 URL to avoid CORS issues
  // The backend endpoint /api/professionals/professionalPicture/:id proxies the image
  let profilePictureUrl: string | undefined
  let pictureId: string | undefined
  if (apiProf.picture && apiProf.picture !== '663181b6e82971d29fc66d46') {
    // Extract picture ID from picture field (can be string or object)
    if (typeof apiProf.picture === 'string') {
      pictureId = apiProf.picture
    } else if (apiProf.picture && typeof apiProf.picture === 'object' && '_id' in apiProf.picture) {
      pictureId = apiProf.picture._id
    }
    // Always use the backend proxy endpoint to avoid CORS/ORB blocking
    profilePictureUrl = `${import.meta.env.VITE_API_BASE_URL}/api/professionals/professionalPicture/${apiProf.id}`
  }

  // Handle phone number with fallback logic:
  // 1. Try professionalContact field first (professional-specific contact)
  // 2. Try contact field as alias
  // 3. Fall back to provider.phoneNumber (user profile phone)
  const contactPhone = apiProf.professionalContact || apiProf.contact || apiProf.provider?.phoneNumber || ''

  return {
    id: apiProf.id,
    userId: '', // Not provided by API
    professionalName: apiProf.name || '',
    bio: apiProf.bio || '', // Not provided by API
    professionId,
    cityId,
    gender: apiProf.gender ?? null,
    contactPhone,
    profilePictureUrl,
    pictureId, // Store the picture ID for custom picture detection
    instagramUrl: apiProf.socials?.instagram,
    facebookUrl: apiProf.socials?.facebook,
    googleMapsUrl: apiProf.socials?.googleMaps,
    activeAccount: apiProf.activeAccount ?? true,
    isActive: apiProf.activeAccount ?? true,
    averageRating: rating,
    totalReviews: apiProf.comments || 0,
    createdAt: '', // Not provided by API
    updatedAt: '', // Not provided by API
    // Profile claiming status (defaults to CLAIMED for backwards compatibility)
    status: apiProf.status || 'CLAIMED',
    // Populated fields
    profession: professionObj,
    city: apiProf.city?.[0]
      ? {
        id: apiProf.city[0].id,
        name: cityName,
        createdAt: '',
        updatedAt: '',
      }
      : undefined,
    // Map provider to user for owner information
    user: apiProf.provider
      ? {
        id: '',
        firstName: apiProf.provider.name || '',
        lastName: apiProf.provider.lastName || '',
        email: '',
        phone: apiProf.provider.phoneNumber || '',
        isEmailValidated: false,
        isPhoneValidated: false,
        isProfessional: false,
        isAccountValidated: false,
        createdAt: '',
        updatedAt: '',
      }
      : undefined,
  }
}

// ============================================================================
// Query Keys
// ============================================================================

export const professionalsKeys = {
  all: ['professionals'] as const,
  lists: () => [...professionalsKeys.all, 'list'] as const,
  list: (filters: ProfessionalFilters) => [...professionalsKeys.lists(), filters] as const,
  details: () => [...professionalsKeys.all, 'detail'] as const,
  detail: (id: string) => [...professionalsKeys.details(), id] as const,
}

// ============================================================================
// Types
// ============================================================================

interface UseProfessionalsOptions extends ProfessionalFilters {
  enabled?: boolean
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get paginated list of professionals with filters
 *
 * @param options - Filter options (professionId, cityId, minRating, search, sortBy, page, limit)
 * @returns Paginated list of professionals with pagination metadata
 *
 * @example
 * const { data, isLoading } = useProfessionals({
 *   professionId: '123',
 *   cityId: '456',
 *   minRating: 4,
 *   sortBy: 'rating-desc',
 *   page: 1,
 *   limit: 12
 * })
 */
export function useProfessionals(options: UseProfessionalsOptions = {}) {
  const {
    professionId,
    cityId,
    gender,
    minRating,
    search,
    sortBy,
    page = 1,
    limit = 12,
    enabled = true,
  } = options

  return useQuery({
    queryKey: professionalsKeys.list({
      professionId,
      cityId: cityId || undefined,
      gender,
      minRating,
      search,
      sortBy,
      page,
      limit,
    }),
    queryFn: async () => {
      // Defense-in-depth: only append cityId when it looks like a Mongo ObjectId
      // (24 hex chars). A city NAME (e.g. "Rosario") must never be sent as cityId,
      // because the backend treats this param as an ObjectId and 500s otherwise.
      const isValidObjectId = (value: string) => /^[0-9a-f]{24}$/i.test(value)
      const cityIdParam = cityId && isValidObjectId(cityId) ? `&cityId=${cityId}` : ''

      // Gender filters server-side; only male/female are valid backend values.
      const genderParam = gender === 'male' || gender === 'female' ? `&gender=${gender}` : ''

      // Use the correct API endpoint from reference implementation
      const url = `/api/search/byProfession?search=${professionId}${cityIdParam}${genderParam}`

      // apiClient.get automatically unwraps the response (returns data.data)
      const apiProfessionals = (await apiClient.get<ApiProfessional[]>(url)) || []

      // Map API response to internal Professional type
      let professionals = apiProfessionals.map(mapApiProfessionalToProfessional)

      // Client-side filtering (minRating, search term)
      if (minRating !== undefined) {
        professionals = professionals.filter(p => p.averageRating >= minRating)
      }

      if (search) {
        const searchLower = search.toLowerCase()
        professionals = professionals.filter(p =>
          p.professionalName.toLowerCase().includes(searchLower)
        )
      }

      // Helper function to check if professional has custom profile picture
      const hasCustomProfilePicture = (professional: Professional): boolean => {
        if (!professional.pictureId) return false
        // Check if the pictureId is different from the default picture ID
        return professional.pictureId !== '663181b6e82971d29fc66d46'
      }

      // Client-side sorting
      if (sortBy) {
        professionals = professionals.sort((a, b) => {
          // First, prioritize professionals with custom profile pictures
          const aHasCustomPicture = hasCustomProfilePicture(a)
          const bHasCustomPicture = hasCustomProfilePicture(b)

          if (aHasCustomPicture && !bHasCustomPicture) return -1
          if (!aHasCustomPicture && bHasCustomPicture) return 1

          // If both have or both don't have custom pictures, apply regular sorting
          switch (sortBy) {
            case 'rating-desc':
              return b.averageRating - a.averageRating
            case 'rating-asc':
              return a.averageRating - b.averageRating
            case 'reviews-desc':
              return b.totalReviews - a.totalReviews
            case 'reviews-asc':
              return a.totalReviews - b.totalReviews
            case 'name-asc':
              return a.professionalName.localeCompare(b.professionalName)
            case 'name-desc':
              return b.professionalName.localeCompare(a.professionalName)
            default:
              return 0
          }
        })
      }

      // Client-side pagination
      const total = professionals.length
      const totalPages = Math.ceil(total / limit)
      const startIndex = (page - 1) * limit
      const endIndex = startIndex + limit
      const paginatedData = professionals.slice(startIndex, endIndex)

      return {
        data: paginatedData,
        pagination: {
          page,
          limit,
          total,
          totalPages,
        },
      }
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes (professionals data changes moderately)
  })
}

/**
 * Get single professional by ID
 *
 * @param id - Professional ID
 * @returns Professional details with populated fields (profession, city, user)
 */
export function useProfessional(id: string) {
  return useQuery({
    queryKey: professionalsKeys.detail(id),
    queryFn: async () => {
      const apiProfessional = await apiClient.get(API_ENDPOINTS.professionals.base + "/" + id)
      return mapApiProfessionalToProfessional(apiProfessional as ApiProfessional)
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get professionals by profession ID (convenience hook)
 *
 * @param professionId - Profession ID
 * @param options - Additional filter options
 * @returns Paginated list of professionals filtered by profession
 */
export function useProfessionalsByProfession(
  professionId: string,
  options: Omit<UseProfessionalsOptions, 'professionId'> = {}
) {
  return useProfessionals({
    ...options,
    professionId,
    enabled: !!professionId && (options.enabled ?? true),
  })
}

/**
 * Get professionals by city ID (convenience hook)
 *
 * @param cityId - City ID
 * @param options - Additional filter options
 * @returns Paginated list of professionals filtered by city
 */
export function useProfessionalsByCity(
  cityId: string,
  options: Omit<UseProfessionalsOptions, 'cityId'> = {}
) {
  return useProfessionals({
    ...options,
    cityId,
    enabled: !!cityId && (options.enabled ?? true),
  })
}
